/* Chart-pack geometry + lit SVG builders (C5, design/specs/chart-pack.md).
 *
 * Hand-rolled SVG in lit, no charting library (spec §5.1): the design-system
 * hard rule (tokens only, no raw hex/px) is unenforceable inside a library's
 * generated DOM, and the same geometry must later be emittable by Python for
 * the server-rendered report (spec §5.2 "one geometry, two emitters"). So the
 * layout math lives in pure functions (unit-tested in charts.test.ts) and the
 * lit builders below only turn a computed layout into `<svg>`.
 *
 * This module currently ships the EMPHASIS horizontal-bar form (CH-4 peer
 * self-citation, per the build-order §5.6 step 1) plus the shared honesty
 * furniture every chart owes (provenance caption, table-view twin, aria
 * takeaway). Colors come only from the --ch-* slot tokens (§2.4/§5.5).
 */
import { html, svg, nothing, type TemplateResult } from 'lit';
import { pct } from '../lib/format.ts';

/* ── shared geometry ── */

/** Path for a rect with ONLY its right corners rounded — bars grow from a
 * square left baseline (mark spec §2.3: "4px rounded data-end, square at the
 * baseline"). Falls back to a plain rect when it's too small to round. */
export function roundedRightRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w, h / 2));
  if (rr === 0 || w <= 0) return `M${x},${y}h${Math.max(0, w)}v${h}h${-Math.max(0, w)}z`;
  return (
    `M${x},${y}` +
    `h${w - rr}` +
    `a${rr},${rr} 0 0 1 ${rr},${rr}` +
    `v${h - 2 * rr}` +
    `a${rr},${rr} 0 0 1 ${-rr},${rr}` +
    `h${-(w - rr)}` +
    `z`
  );
}

/** Truncate a label to a max character budget with an ellipsis (SVG <text> has
 * no text-overflow; series names are untrusted data so this is display-only —
 * the full name always lives in the aria table twin). */
export function truncLabel(s: string, max = 22): string {
  const t = (s || '').trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
}

/* ── emphasis bars (CH-4) ── */

export interface EmphasisRow {
  label: string;
  /** 0..1 share. */
  value: number;
  /** the one highlighted series (the client) — every other row is context. */
  highlight?: boolean;
}

export interface EmphasisMark {
  label: string;
  fullLabel: string;
  value: number;
  valueLabel: string;
  highlight: boolean;
  y: number;
  barX: number;
  barW: number;
  barH: number;
  labelX: number;
  valueX: number;
  textY: number;
}

export interface EmphasisLayout {
  marks: EmphasisMark[];
  width: number;
  height: number;
  labelCol: number;
}

const EM_DEFAULTS = { width: 640, labelCol: 190, rowH: 16, gap: 10, top: 6, valuePad: 8 };

/** Pure layout for the emphasis horizontal-bar form. Bars scale to the max
 * value in the set (floored at 0.01 so an all-zero set doesn't divide by zero
 * or paint full-width bars). Deterministic → unit-tested. */
export function emphasisLayout(rows: EmphasisRow[], opts: Partial<typeof EM_DEFAULTS> = {}): EmphasisLayout {
  const o = { ...EM_DEFAULTS, ...opts };
  const max = Math.max(0.01, ...rows.map((r) => r.value || 0));
  const plotW = o.width - o.labelCol - 56; // reserve right room for value labels
  const marks: EmphasisMark[] = rows.map((r, i) => {
    const y = o.top + i * (o.rowH + o.gap);
    const barW = Math.max(0, ((r.value || 0) / max) * plotW);
    return {
      label: truncLabel(r.label),
      fullLabel: (r.label || '').trim(),
      value: r.value || 0,
      valueLabel: `${pct(r.value)}%`,
      highlight: !!r.highlight,
      y,
      barX: o.labelCol,
      barW,
      barH: o.rowH,
      labelX: o.labelCol - 10,
      valueX: o.labelCol + barW + o.valuePad,
      textY: y + o.rowH / 2,
    };
  });
  return {
    marks,
    width: o.width,
    height: o.top + rows.length * (o.rowH + o.gap) + 2,
    labelCol: o.labelCol,
  };
}

/** The emphasis-bar SVG. Client bar = --ch-1 (azure), every peer = --ch-peer
 * gray (emphasis form §2.4). role="list" (NOT "img") with the takeaway as its
 * aria-label — the rows are focusable listitems, and role="img" would be an
 * atomic image that must not contain focusable descendants (Design ARIA
 * ruling). Text never wears the series color (§2.1). */
export function emphasisBarsSvg(layout: EmphasisLayout, ariaLabel: string): TemplateResult {
  return html`<svg
    class="chart chart-emph"
    viewBox="0 0 ${layout.width} ${layout.height}"
    width="100%"
    role="list"
    aria-label=${ariaLabel}
    preserveAspectRatio="xMinYMin meet"
  >
    ${layout.marks.map(
      (m) => svg`<g class="emph-row" tabindex="0" role="listitem"
        aria-label="${m.fullLabel}: ${m.valueLabel}${m.highlight ? ' (client)' : ''}">
        <title>${m.fullLabel}: ${m.valueLabel}${m.highlight ? ' (client)' : ''}</title>
        <text class="emph-name" x=${m.labelX} y=${m.textY} text-anchor="end" dominant-baseline="middle">${m.label}</text>
        <path class="emph-bar ${m.highlight ? 'is-client' : ''}" d=${roundedRightRectPath(m.barX, m.y, m.barW, m.barH, 4)}></path>
        <text class="emph-val" x=${m.valueX} y=${m.textY} dominant-baseline="middle">${m.valueLabel}</text>
      </g>`,
    )}
  </svg>`;
}

/* ── horizontal stacked bar (CH-3 ownership mix) ── */

export interface StackSeg {
  key: string;
  label: string;
  value: number;
}

export interface StackMark {
  key: string;
  /** category name — announced in the segment's aria/title ("Owned: 6 (60%)"). */
  label: string;
  value: number;
  /** share of the total, percent (for the aria/title). */
  share: number;
  /** the count rendered INSIDE the bar (distinct from the name). */
  countLabel: string;
  x: number;
  w: number;
  /** true when the count label fits inside the segment with padding (§2.3 fit
   * rule); otherwise the legend/table carry it — never overflow:hidden. */
  labelFits: boolean;
  textX: number;
}

export interface StackLayout {
  marks: StackMark[];
  width: number;
  height: number;
  barH: number;
  total: number;
}

const STACK_DEFAULTS = { width: 640, barH: 20, gap: 2, top: 2 };

/** Pure layout for the horizontal stacked bar. Zero-value segments are dropped
 * from the BAR (they have no width) — the legend still lists them at 0 (§CH-3
 * "never a hidden category"), which is the caller's job. Deterministic. */
export function stackedBarLayout(segs: StackSeg[], opts: Partial<typeof STACK_DEFAULTS> = {}): StackLayout {
  const o = { ...STACK_DEFAULTS, ...opts };
  const shown = segs.filter((s) => (s.value || 0) > 0);
  const total = shown.reduce((a, s) => a + (s.value || 0), 0);
  const gaps = Math.max(0, shown.length - 1) * o.gap;
  const plotW = Math.max(0, o.width - gaps);
  let x = 0;
  const marks: StackMark[] = shown.map((s) => {
    const w = total > 0 ? (s.value / total) * plotW : 0;
    const countLabel = String(s.value);
    // ~7px per glyph at the 11px count size + 10px padding, in viewBox units
    const labelFits = w >= countLabel.length * 7 + 10;
    const mark: StackMark = {
      key: s.key,
      label: s.label,
      value: s.value,
      share: total > 0 ? Math.round((s.value / total) * 100) : 0,
      countLabel,
      x,
      w,
      labelFits,
      textX: x + w / 2,
    };
    x += w + o.gap;
    return mark;
  });
  return { marks, width: o.width, height: o.top + o.barH + o.top, barH: o.barH, total };
}

/** The stacked-bar SVG. Segment fills come from per-key CSS classes
 * (`seg-${key}` → CAT_COLOR tokens), never inline hex — 2px gaps show the card
 * surface through (§2.3 "the gap separates, never a stroke"), outer corners
 * rounded via a clip. Count labels render inside only when they fit. */
export function stackedBarSvg(layout: StackLayout, ariaLabel: string, clipId = 'ch3clip'): TemplateResult {
  const y = (layout.height - layout.barH) / 2;
  // role="list" (not "img") — the segments are focusable listitems; role="img"
  // means an atomic image and must not contain focusable descendants (Design
  // ARIA ruling, applied consistently with CH-4).
  return html`<svg
    class="chart chart-stack"
    viewBox="0 0 ${layout.width} ${layout.height}"
    width="100%"
    role="list"
    aria-label=${ariaLabel}
    preserveAspectRatio="xMinYMin meet"
  >
    <defs>
      <clipPath id=${clipId}><rect x="0" y=${y} width=${layout.width} height=${layout.barH} rx="4"></rect></clipPath>
    </defs>
    <g clip-path="url(#${clipId})">
      ${layout.marks.map(
        (m) => svg`<g class="stack-seg seg-${m.key}" tabindex="0" role="listitem"
          aria-label="${m.label}: ${m.value} (${m.share}%)">
          <title>${m.label}: ${m.value} (${m.share}%)</title>
          <rect class="stack-fill" x=${m.x} y=${y} width=${m.w} height=${layout.barH}></rect>
          ${m.labelFits
            ? svg`<text class="stack-lbl" x=${m.textX} y=${y + layout.barH / 2} text-anchor="middle" dominant-baseline="middle">${m.countLabel}</text>`
            : nothing}
        </g>`,
      )}
    </g>
  </svg>`;
}

/* ── percent line (CH-1 page-1 ownership % trend; "share of voice" is NOT an FB concept — owner ruling 2026-07-12) ── */

export interface LinePoint {
  /** ISO date (x); rendered as mm-dd ticks. */
  date: string;
  /** share 0..1 (y = % of page-1 slots owned). */
  value: number;
  /** Undesirable count at this poll — a sub-track mark, never a y-series (§2.2). */
  sub?: number;
}

export interface LinePointMark {
  x: number;
  y: number;
  value: number;
  valueLabel: string;
  date: string;
  sub: number;
}

export interface PercentLineLayout {
  points: LinePointMark[];
  /** may contain multiple sub-paths (extra `M`s) — the line BREAKS across a
   * polling hole rather than interpolating fabricated data (§3.2). */
  linePath: string;
  gridY: Array<{ y: number; label: string }>;
  xticks: Array<{ x: number; label: string }>;
  subMarks: Array<{ x: number; count: number }>;
  /** date holes detected between consecutive polls — rendered as a footnote
   * ("no capture 05-14 → 05-28"), never bridged by a line segment. */
  holes: Array<{ from: string; to: string }>;
  axisY: number;
  subY: number;
  /** plot extents — carried on the layout (not hardcoded in the emitter) so the
   * TS + Python emitters stay in D3 parity. */
  plotLeft: number;
  plotRight: number;
  yLabelX: number;
  width: number;
  height: number;
  /** the endpoint (last point) — where the series value label anchors. */
  endpoint: LinePointMark | null;
}

const LINE_DEFAULTS = { width: 640, height: 260, padL: 40, padR: 54, plotTop: 12, plotH: 184 };

/** mm-dd from an ISO date, defensively (the string is IMPACT data). */
function mmdd(date: string): string {
  const s = String(date || '');
  return s.length >= 10 ? s.slice(5, 10) : s;
}

/** Whole-day gap between two ISO dates (0 when unparseable — defensive over
 * IMPACT data). */
function dayGap(a: string, b: string): number {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isFinite(ta) && Number.isFinite(tb) ? Math.round((tb - ta) / 86400000) : 0;
}

/** Pure layout for the SoV% line. y-axis is ALWAYS 0–100% (§2.2 — no zoomed
 * range that turns a wiggle into a cliff). Points are spaced by index; a single
 * point plots as one marker with no line. A polling HOLE (a day-gap notably
 * larger than the series cadence) BREAKS the line — the path restarts with a
 * new `M` and the hole is reported for the footnote — rather than interpolating
 * fabricated data across it (§3.2). Deterministic → unit-tested. */
export function percentLineLayout(points: LinePoint[], opts: Partial<typeof LINE_DEFAULTS> = {}): PercentLineLayout {
  const o = { ...LINE_DEFAULTS, ...opts };
  const plotLeft = o.padL;
  const plotRight = o.width - o.padR;
  const plotW = plotRight - plotLeft;
  const axisY = o.plotTop + o.plotH;
  const subY = axisY + 40;
  const n = points.length;
  const xAt = (i: number) => (n <= 1 ? plotLeft + plotW / 2 : plotLeft + (i / (n - 1)) * plotW);
  const yAt = (v: number) => o.plotTop + (1 - Math.max(0, Math.min(1, v))) * o.plotH;

  const marks: LinePointMark[] = points.map((p, i) => ({
    x: xAt(i),
    y: yAt(p.value || 0),
    value: p.value || 0,
    valueLabel: `${pct(p.value)}%`,
    date: p.date,
    sub: p.sub || 0,
  }));

  // Hole detection: a gap between consecutive polls > 2× the median cadence is
  // a hole. `holeAfter[i]` = true when the segment from point i to i+1 spans a
  // hole (so the line must not be drawn across it).
  const gaps: number[] = [];
  for (let i = 1; i < n; i++) { const a = points[i - 1], b = points[i]; if (a && b) gaps.push(dayGap(a.date, b.date)); }
  const sortedGaps = [...gaps].sort((a, b) => a - b);
  const median = sortedGaps.length ? (sortedGaps[Math.floor(sortedGaps.length / 2)] ?? 0) : 0;
  const holeAfter = (i: number) => median > 0 && (gaps[i] ?? 0) > 2 * median;
  const holes: Array<{ from: string; to: string }> = [];
  for (let i = 0; i < gaps.length; i++) { const a = points[i], b = points[i + 1]; if (holeAfter(i) && a && b) holes.push({ from: a.date, to: b.date }); }

  const linePath = marks.length >= 2
    ? marks
        .map((m, i) => {
          const cmd = i === 0 || holeAfter(i - 1) ? 'M' : 'L'; // restart after a hole
          return `${cmd}${m.x.toFixed(1)},${m.y.toFixed(1)}`;
        })
        .join(' ')
    : '';

  const gridY = [0, 0.25, 0.5, 0.75, 1].map((v) => ({ y: yAt(v), label: `${Math.round(v * 100)}%` }));

  // x ticks: first, last, and (for ≥5 points) the midpoint — never every date.
  const tickIdx = n <= 1 ? [0] : n < 5 ? [0, n - 1] : [0, Math.floor((n - 1) / 2), n - 1];
  const xticks = n === 0 ? [] : [...new Set(tickIdx)].map((i) => ({ x: xAt(i), label: mmdd(points[i]?.date ?? '') }));

  const subMarks = marks.filter((m) => m.sub > 0).map((m) => ({ x: m.x, count: m.sub }));

  return {
    points: marks,
    linePath,
    gridY,
    xticks,
    subMarks,
    holes,
    axisY,
    subY,
    plotLeft,
    plotRight,
    yLabelX: plotLeft - 6,
    width: o.width,
    height: o.height,
    endpoint: marks.length ? marks[marks.length - 1] ?? null : null,
  };
}

/** The SoV% line SVG: horizontal gridlines + 0–100% y-labels, one --ch-1 line,
 * markers (r4 + white ring) on every real poll, an endpoint value label, an
 * x-axis with mm-dd ticks, and the Undesirable sub-track (--imp-neg dots +
 * "▼n") below the axis (§CH-1 adverse marks — IMPACT-mirror hue, never a line
 * series). Focusable markers carry a per-point aria-label. */
export function percentLineSvg(layout: PercentLineLayout, ariaLabel: string): TemplateResult {
  const ep = layout.endpoint;
  // role="list" (not "img"): the markers are focusable listitems; role="img"
  // is an atomic image and must not contain focusable descendants (Design ARIA
  // ruling, applied consistently across the pack).
  return html`<svg
    class="chart chart-line"
    viewBox="0 0 ${layout.width} ${layout.height}"
    width="100%"
    role="list"
    aria-label=${ariaLabel}
    preserveAspectRatio="xMinYMin meet"
  >
    ${layout.gridY.map(
      (g) => svg`<g class="ln-grid" role="presentation">
        <line x1=${layout.plotLeft} y1=${g.y} x2=${layout.plotRight} y2=${g.y}></line>
        <text class="ln-ytick" x=${layout.yLabelX} y=${g.y} text-anchor="end" dominant-baseline="middle">${g.label}</text>
      </g>`,
    )}
    <line class="ln-axis" x1=${layout.plotLeft} y1=${layout.axisY} x2=${layout.plotRight} y2=${layout.axisY}></line>
    ${layout.xticks.map(
      (t) => svg`<text class="ln-xtick" x=${t.x} y=${layout.axisY + 16} text-anchor="middle">${t.label}</text>`,
    )}
    ${layout.linePath ? svg`<path class="ln-path" d=${layout.linePath}></path>` : nothing}
    ${layout.points.map(
      (m) => svg`<circle class="ln-dot" cx=${m.x} cy=${m.y} r="4" tabindex="0" role="listitem"
        aria-label="${mmdd(m.date)}: ${m.valueLabel} owned${m.sub ? `, ${m.sub} undesirable` : ''}"><title>${mmdd(m.date)}: ${m.valueLabel}</title></circle>`,
    )}
    ${ep ? svg`<text class="ln-endlbl" x=${ep.x + 8} y=${ep.y} dominant-baseline="middle">${ep.valueLabel}</text>` : nothing}
    ${layout.subMarks.length
      ? svg`<text class="ln-subttl" x=${layout.plotLeft} y=${layout.subY - 14}>Undesirable on page 1</text>
          ${layout.subMarks.map(
            (s) => svg`<g class="ln-sub"><circle cx=${s.x} cy=${layout.subY} r="3"></circle><text x=${s.x} y=${layout.subY + 14} text-anchor="middle">▼${s.count}</text></g>`,
          )}`
      : nothing}
  </svg>`;
}

/* ── shared honesty furniture ── */

/** Provenance caption (§3.1) — one muted line naming measurement · source ·
 * window. Printed, never hover-only. */
export function chartCaption(text: string): TemplateResult {
  return html`<div class="chart-cap">${text}</div>`;
}

/** Table-view twin (§5.4, an a11y non-negotiable): a <details> holding the
 * exact values as a real table so nothing lives only in a mark or tooltip. */
export function chartTableTwin(
  cols: string[],
  rows: Array<Array<string | number>>,
): TemplateResult {
  return html`<details class="chart-table">
    <summary>View as table</summary>
    <div class="chart-table-wrap">
      <table>
        <thead><tr>${cols.map((c, i) => html`<th scope="col" class=${i === 0 ? '' : 'num'}>${c}</th>`)}</tr></thead>
        <tbody>${rows.map(
          (r) => html`<tr>${r.map((cell, i) => (i === 0
            ? html`<th scope="row">${cell}</th>`
            : html`<td class="num">${cell}</td>`))}</tr>`,
        )}</tbody>
      </table>
    </div>
  </details>`;
}

/** Empty/dark-state line for a chart card — a caption, never a zeroed axis
 * (§3.5). */
export function chartEmpty(text: string): TemplateResult {
  return html`<div class="chart-cap chart-empty">${text || nothing}</div>`;
}
